import type { HintDepth } from "./hints.ts";
import { hintCreditLabel } from "./hints.ts";
import {
  describeEngineChoices,
  type EngineChoice,
  type MedalName,
  type WithheldReason,
} from "./medals.ts";
import { QUALITY_BANDS, type QualityBandId } from "./quality.ts";
import { readPosition, type PositionReading } from "./positionReading.ts";
import { newlyAchievedThemes, themeSummary, type ThemeStatus } from "./themes.ts";
import { Chess } from "@vendor/chess.js";
import { cleanName, ON_BOOK, type OpeningPathKind } from "./openingPath.ts";

/**
 * The five-move report card: a structured pause after every five student
 * moves. It is built from what the session already knows (bands, medals,
 * book status, engine evaluations, authored lessons, course themes and a
 * reading of the board) and never invents theory or an engine ranking.
 */

export type ReportCardMove = {
  moveNumber: number;
  san: string;
  fenBefore: string;
  qualityBand: QualityBandId;
  medal: MedalName | null;
  earnedMedal: MedalName | null;
  rank: number | null;
  rankTotal?: number | null;
  percentile?: number | null;
  isMainline: boolean;
  bookStatus: string;
  /** The same authored evidence used by the Book Trail; independent of engine rank. */
  bookKind?: OpeningPathKind;
  assisted: boolean;
  deepestHint: HintDepth;
  withheld: WithheldReason;
  recommendedSan: string | null;
  /**
   * The course's move in this position: null when the course had none (the
   * end of a line, free play), undefined when not recorded.
   */
  courseSan?: string | null;
  engineBestSan: string | null;
  /** Stockfish's first three choices in the position, when the ranking search has them. */
  engineChoices?: EngineChoice[];
  cpLoss: number | null;
  evalAfterCp: number | null;
  concepts: Array<{ tag: string; polarity: string }>;
  reason: string;
  lessonWhy?: string | null;
  lessonTakeaway?: string | null;
  courseMoveWhy?: string | null;
  /** The book's name for this move when it is a recognized alternative. */
  variation?: string | null;
};

export type UpcomingLesson = {
  moveNumber: number;
  san: string;
  goal: string;
  watch: string;
};

export type ReportCardInput = {
  openingId: string;
  openingTitle: string;
  playerSide?: "w" | "b";
  windowIndex: number;
  fromMove: number;
  toMove: number;
  moves: ReportCardMove[];
  blackReplies: string[];
  fenStart: string;
  fenEnd: string;
  themesBefore: ThemeStatus[];
  themesAfter: ThemeStatus[];
  upcoming: UpcomingLesson[];
  /** False in a side line: the course's main-line ideas don't apply there. */
  onMainline?: boolean;
  nextDecision: string | null;
  guide: { goal: string; piece: string; next: string; watch: string } | null;
  middlegamePlan: string | null;
  mode: "learn" | "recall" | "branches" | "review";
  freePlay: boolean;
  finalWindow: boolean;
  engineChecked: boolean;
  gameOver: boolean;
  gameResult?: "win" | "loss" | "stalemate" | "draw" | null;
  /** Whether the game was still on the course line when this window began. */
  startedOnCourse?: boolean;
};

export type GradeWord = "Composed" | "Steady" | "Searching" | "Unsettled";

export type ReportCardItem = { title: string; detail: string; moveNumber?: number };

export type ReportCard = {
  id: string;
  playerSide?: "w" | "b";
  windowIndex: number;
  fromMove: number;
  toMove: number;
  title: string;
  openingTitle: string;
  grade: { word: GradeWord; score: number; summary: string };
  headline: string;
  moves: Array<{
    moveNumber: number;
    san: string;
    band: QualityBandId;
    bandLabel: string;
    medal: MedalName | null;
    rank: number | null;
    rankTotal: number | null;
    inBook: boolean;
    bookLabel?: string | null;
    assisted: boolean;
    reply: string | null;
  }>;
  trend: {
    available: boolean;
    points: Array<{ moveNumber: number; cp: number }>;
    summary: string;
  };
  strengths: ReportCardItem[];
  weaknesses: ReportCardItem[];
  reflections: string[];
  reading: PositionReading;
  themes: {
    done: number;
    total: number;
    newlyAchieved: ThemeStatus[];
    achieved: ThemeStatus[];
    pending: ThemeStatus[];
    missed: ThemeStatus[];
  };
  nextFive: { headline: string; steps: string[]; habit: string };
  closing: string;
  finalWindow: boolean;
  freePlay: boolean;
  gameOver: boolean;
};

const BAND_SCORE: Record<QualityBandId, number> = {
  "best-practical": 100,
  "strong-alternative": 86,
  playable: 66,
  questionable: 32,
  "tactical-error": 0,
  "not-analyzed": 0,
};

const CONCEPT_NOTE: Record<string, { title: string; detail: string }> = {
  "piece-twice": {
    title: "A piece moved twice while others waited",
    detail:
      "What did the second move achieve? Compare it with developing a new piece, allowing for threats that need an immediate answer.",
  },
  "early-queen": {
    title: "The queen came out early",
    detail:
      "Can the opponent develop while attacking your queen? Its immediate threat must justify that lost time.",
  },
  "flank-pawn": {
    title: "A flank pawn before the center",
    detail:
      "Name the pawn’s job: restraining a piece, preparing a break, or answering a threat. Was it more urgent than development?",
  },
};

function moveScore(move: ReportCardMove): number {
  let base = BAND_SCORE[move.qualityBand];
  if (move.qualityBand === "not-analyzed") {
    base = move.isMainline ? 84 : onPreparedRoad(move) ? 72 : 60;
  }
  if (move.isMainline && move.qualityBand !== "tactical-error") base = Math.min(100, base + 4);
  if (move.assisted) base *= 0.85;
  return base;
}

function gradeWord(score: number): GradeWord {
  if (score >= 88) return "Composed";
  if (score >= 72) return "Steady";
  if (score >= 55) return "Searching";
  return "Unsettled";
}

function plural(count: number, singular: string, pluralWord = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralWord}`;
}

function joinWords(items: string[]): string {
  if (items.length <= 1) return items[0] || "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** The sentence that teaches, skipping the label "c4 is an acceptable alternative: Name." */
function usefulWhy(text: string | null | undefined): string {
  const first = firstSentence(text);
  if (!first || !/acceptable alternative/i.test(first)) return first;
  return firstSentence((text || "").slice(first.length).trim());
}

function formatCp(cp: number): string {
  if (Math.abs(cp) >= 50_000) return cp > 0 ? "mate in your favor" : "mate against you";
  const pawns = cp / 100;
  const sign = pawns > 0 ? "+" : "";
  return `${sign}${pawns.toFixed(1)}`;
}

function moveRef(move: { moveNumber: number; san: string }): string {
  return `Move ${move.moveNumber} (${move.san})`;
}

/**
 * The first sentence. A move annotation such as "…Bxc2??" or "Nf3!" is part
 * of the sentence, not its end.
 */
function firstSentence(text: string | null | undefined): string {
  if (!text) return "";
  const end = /[.!?]+(?=\s|$)/g;
  for (let match = end.exec(text); match; match = end.exec(text)) {
    const before = text[match.index - 1] || "";
    const annotation = match[0] !== "." && /[a-h1-8NBRQKO+#]/.test(before);
    if (!annotation) return text.slice(0, match.index + match[0].length).trim();
  }
  return text.trim();
}

/** The course line, a transposition, or a named alternative the book accepts. */
function onPreparedRoad(move: ReportCardMove): boolean {
  if (move.bookKind) return ON_BOOK.has(move.bookKind);
  return move.isMainline || move.bookStatus === "known" || move.bookStatus === "transposition";
}

function variationName(move: ReportCardMove): string | null {
  return cleanName(move.variation);
}

/** The move that first left a prepared road, while the game was still on one. */
function firstDeparture(input: ReportCardInput): ReportCardMove | null {
  let onCourse = input.startedOnCourse ?? true;
  for (const move of input.moves) {
    const courseHadMove = move.courseSan !== null;
    const offCourse = courseHadMove && !onPreparedRoad(move);
    if (onCourse && offCourse) return move;
    if (onPreparedRoad(move)) onCourse = true;
    else if (offCourse || !courseHadMove) onCourse = false;
  }
  return null;
}

function latestAlternative(input: ReportCardInput): ReportCardMove | null {
  // A past alternative is worth praising, but is no longer a plan for the
  // current board once a later move leaves the prepared coverage.
  for (const move of [...input.moves].reverse()) {
    if (!onPreparedRoad(move)) return null;
    if (!move.isMainline) return move;
  }
  return null;
}

function gameResult(input: ReportCardInput): ReportCardInput["gameResult"] {
  if (!input.gameOver) return null;
  if (input.gameResult) return input.gameResult;
  const board = new Chess(input.fenEnd);
  if (board.isCheckmate()) return board.turn() === (input.playerSide ?? "w") ? "loss" : "win";
  if (board.isStalemate()) return "stalemate";
  return board.isDraw() ? "draw" : null;
}

/** "five" when the window is the usual length, otherwise the actual count. */
function spanWord(count: number): string {
  return count === 5 ? "five" : String(count);
}

function choiceNote(move: ReportCardMove): string {
  if (!move.engineChoices || move.engineChoices.length < 2) return "";
  return describeEngineChoices(move.san, move.engineChoices, move.cpLoss);
}

function betterMoveNote(move: ReportCardMove): string {
  const course = move.courseSan === undefined ? move.recommendedSan : move.courseSan;
  const choices = choiceNote(move);
  if (course && move.courseMoveWhy) {
    const base = `The course move was ${course}: ${firstSentence(move.courseMoveWhy)}`;
    return choices ? `${base} ${choices}` : base;
  }
  if (course && choices) return `The course move was ${course}. ${choices}`;
  if (course && move.engineBestSan && course !== move.engineBestSan) {
    return `The course move was ${course}; the engine preferred ${move.engineBestSan}.`;
  }
  if (course) return `The course move was ${course}.`;
  if (choices) return choices;
  if (move.engineBestSan && move.engineBestSan !== move.san) {
    return `The engine preferred ${move.engineBestSan}.`;
  }
  return "";
}

function buildStrengths(
  input: ReportCardInput,
  readingStart: PositionReading,
  readingEnd: PositionReading
): ReportCardItem[] {
  const { moves } = input;
  const candidates: Array<ReportCardItem & { priority: number }> = [];
  const medalled = moves.filter((m) => m.medal);
  if (medalled.length) {
    candidates.push({
      priority: 82,
      title:
        medalled.length === 1
          ? `A top-three choice on move ${medalled[0].moveNumber}`
          : `${plural(medalled.length, "top-three choice")} in ${spanWord(moves.length)} moves`,
      detail:
        "These moves were independently ranked in Stockfish’s top three and found without hints or take-backs.",
    });
  }
  const inBook = moves.filter((m) => m.isMainline);
  if (inBook.length === moves.length && moves.length > 0) {
    candidates.push({
      priority: 90,
      title: "Every move in the course line",
      detail: `You stayed with the course for all ${spanWord(moves.length)} moves. Try explaining the purpose of each move before replaying the sequence.`,
    });
  } else if (inBook.length >= 3) {
    candidates.push({
      priority: 60,
      title: `On course for ${inBook.length} of ${spanWord(moves.length)} moves`,
      detail: `The course line was followed on ${inBook.map((m) => `move ${m.moveNumber}`).join(", ")}.`,
    });
  }
  const alternatives = moves.filter((move) => !move.isMainline && onPreparedRoad(move));
  if (alternatives.length) {
    const named =
      [...alternatives].reverse().find((move) => variationName(move)) || alternatives.at(-1)!;
    const name = variationName(named);
    const why = usefulWhy(named.lessonWhy || named.reason);
    candidates.push({
      priority: 94,
      moveNumber: named.moveNumber,
      title: name
        ? `A recognized alternative: ${name}`
        : `A recognized alternative on move ${named.moveNumber}`,
      detail: `${moveRef(named)} follows ${name || "a prepared alternative"}.${
        why ? ` ${why}` : ""
      }`,
    });
  }
  const unaided = moves.filter((m) => !m.assisted);
  if (unaided.length === moves.length && moves.length > 0) {
    candidates.push({
      priority: 70,
      title: "Found without help",
      detail: `No hints or take-backs across these ${spanWord(moves.length)} moves. Replay them and explain each decision.`,
    });
  }
  const best = [...moves].reverse().find((m) => onPreparedRoad(m) && m.isMainline && m.lessonWhy);
  if (best) {
    candidates.push({
      priority: 95,
      moveNumber: best.moveNumber,
      title: `The idea worth remembering: ${moveRef(best)}`,
      detail: [usefulWhy(best.lessonWhy), best.lessonTakeaway].filter(Boolean).join(" "),
    });
  }
  const castled = moves.find((m) => /^O-O/.test(m.san));
  if (castled) {
    candidates.push({
      priority: 86,
      moveNumber: castled.moveNumber,
      title: `Castling on move ${castled.moveNumber}`,
      detail:
        readingEnd.character === "open" || readingEnd.pawnsTraded
          ? "The center is already open now. Castling moved your king off the central files and activated a rook. Keep checking the lines toward its new square."
          : readingEnd.character === "closed"
            ? "With the center closed now, castling prepared king and rook for future pawn breaks. Check the pawn cover before opening a file."
            : "Castling moved the king off the central files and activated a rook. Keep its pawn cover and nearby defenders intact.",
    });
  }
  const minorsLeft = (reading: PositionReading) => reading.development.whiteUndeveloped.length;
  if (
    minorsLeft(readingStart) > 0 &&
    minorsLeft(readingEnd) === 0 &&
    readingEnd.development.whiteTotal > 0
  ) {
    const total = readingEnd.development.whiteTotal;
    const pieces =
      total === 4
        ? "All four minor pieces"
        : total === 1
          ? "Your remaining minor piece"
          : `All ${total} remaining minor pieces`;
    candidates.push({
      priority: 87,
      title: "Development complete",
      detail: `${pieces} ${total === 1 ? "is" : "are"} now in play${readingEnd.development.whiteRooksConnected ? " and the rooks are connected" : ""}. Compare their jobs: which piece would benefit most from a better square?`,
    });
  }
  const unlocked = newlyAchievedThemes(input.themesBefore, input.themesAfter);
  for (const theme of unlocked.slice(0, 2)) {
    candidates.push({
      priority: 75,
      title: `Course idea realized: ${theme.title}`,
      detail: theme.idea,
    });
  }
  for (let i = 0; i < moves.length - 1; i += 1) {
    const slip = moves[i];
    if (slip.qualityBand !== "questionable" && slip.qualityBand !== "tactical-error") continue;
    const after = moves.slice(i + 1);
    if (
      after.length >= 2 &&
      after.every(
        (m) => m.qualityBand === "best-practical" || m.qualityBand === "strong-alternative"
      )
    ) {
      candidates.push({
        priority: 58,
        moveNumber: slip.moveNumber,
        title: `Steadied after move ${slip.moveNumber}`,
        detail: `After ${slip.san}, the remaining moves received stronger grades. Find what changed in your decision process so you can repeat it.`,
      });
      break;
    }
  }
  const seen = new Set<string>();
  return candidates
    .sort((a, b) => b.priority - a.priority)
    .filter((item) => {
      if (seen.has(item.title)) return false;
      seen.add(item.title);
      return true;
    })
    .slice(0, 3)
    .map(({ title, detail, moveNumber }) =>
      moveNumber ? { title, detail, moveNumber } : { title, detail }
    );
}

function buildWeaknesses(input: ReportCardInput, readingEnd: PositionReading): ReportCardItem[] {
  const { moves } = input;
  const candidates: Array<ReportCardItem & { priority: number }> = [];
  let onCourse = input.startedOnCourse ?? true;
  for (const move of moves) {
    // Where the course has no move (the end of a line, free play), nothing
    // can be left.
    const courseHadMove = move.courseSan !== null;
    const offCourse = courseHadMove && !onPreparedRoad(move);
    const departure = onCourse && offCourse;
    if (onPreparedRoad(move)) onCourse = true;
    else if (offCourse || !courseHadMove) onCourse = false;
    if (move.qualityBand === "tactical-error") {
      candidates.push({
        priority: 100,
        moveNumber: move.moveNumber,
        title: `${moveRef(move)} ran into a tactic`,
        detail: `${firstSentence(move.reason)} ${betterMoveNote(move)}`.trim(),
      });
    } else if (move.qualityBand === "questionable") {
      candidates.push({
        priority: 85,
        moveNumber: move.moveNumber,
        title: `${moveRef(move)} gave something away`,
        detail: `${firstSentence(move.reason)} ${betterMoveNote(move)}`.trim(),
      });
    } else if (departure) {
      const sound =
        move.qualityBand === "best-practical" || move.qualityBand === "strong-alternative";
      candidates.push({
        priority: sound ? 40 : 70,
        moveNumber: move.moveNumber,
        title: sound
          ? `${moveRef(move)} was sound but left the course`
          : `${moveRef(move)} left the course`,
        detail: `${betterMoveNote(move) || firstSentence(move.reason)} ${
          sound ? "Reassess the plan against this new position." : ""
        }`.trim(),
      });
    }
  }
  const hinted = moves.filter((m) => m.deepestHint > 0);
  if (hinted.length) {
    const deepest = [...hinted].sort((a, b) => b.deepestHint - a.deepestHint)[0];
    candidates.push({
      priority: 60,
      title: hinted.length === 1 ? "One move needed a hint" : `${hinted.length} moves needed hints`,
      detail: `${moveRef(deepest)} went to the ${hintCreditLabel(deepest.deepestHint).replace("Assisted · ", "")} step. Next time, say the goal and the piece out loud before opening the ladder.`,
    });
  }
  const retried = moves.filter((m) => m.withheld === "takeback");
  if (retried.length) {
    candidates.push({
      priority: 50,
      title:
        retried.length === 1
          ? "One move was taken back"
          : `${retried.length} moves were taken back`,
      detail:
        "The medal is withheld after a take-back. Practice predicting the reply before committing to the move.",
    });
  }
  const conceptCounts: Record<string, number> = {};
  for (const move of moves) {
    // A prepared road is never a bad habit, whatever the legacy tags say.
    if (onPreparedRoad(move) || (move.cpLoss != null && move.cpLoss <= 30)) continue;
    for (const concept of move.concepts) {
      if (concept.polarity !== "negative") continue;
      conceptCounts[concept.tag] = (conceptCounts[concept.tag] || 0) + 1;
    }
  }
  for (const [tag, count] of Object.entries(conceptCounts)) {
    const note = CONCEPT_NOTE[tag];
    if (!note) continue;
    candidates.push({
      priority: 55 + count * 5,
      title: note.title,
      detail: note.detail,
    });
  }
  const missedBefore = new Set(
    input.themesBefore.filter((t) => t.state === "missed").map((t) => t.id)
  );
  // An idea is only the student's loss when the student's own move left the
  // course; a side line the opponent chose is not something that held them back.
  const studentLeft = input.moves.some(
    (move) => !onPreparedRoad(move) && (move.courseSan ?? move.recommendedSan)
  );
  for (const theme of input.themesAfter
    .filter((t) => studentLeft && t.state === "missed" && !missedBefore.has(t.id))
    .slice(0, 2)) {
    candidates.push({
      priority: 65,
      title: `No longer available: ${theme.title}`,
      detail: `${theme.idea} This game will need a different route to the same goal.`,
    });
  }
  if (readingEnd.looseWhite.length && !readingEnd.inCheck) {
    candidates.push({
      priority: 45,
      title: `Loose right now: ${readingEnd.looseWhite.join(", ")}`,
      detail: "Check whether it can be captured safely before choosing a quiet move.",
    });
  }
  const seen = new Set<string>();
  const ranked = candidates
    .sort((a, b) => b.priority - a.priority)
    .filter((item) => {
      if (seen.has(item.title)) return false;
      seen.add(item.title);
      return true;
    })
    .slice(0, 3)
    .map(({ title, detail, moveNumber }) =>
      moveNumber ? { title, detail, moveNumber } : { title, detail }
    );
  if (ranked.length) return ranked;
  return [
    {
      title: input.engineChecked ? "No clear error flagged" : "Keep the tactical check independent",
      detail: !input.engineChecked
        ? "Book recognition confirms the lesson; the engine has not checked it. Examine the opponent’s forcing replies yourself."
        : "A lower rank alone is not a mistake. Keep identifying the opponent’s idea before choosing your reply.",
    },
  ];
}

function buildReflections(
  input: ReportCardInput,
  readingEnd: PositionReading,
  weaknesses: ReportCardItem[]
): string[] {
  if (input.gameOver) {
    const result = gameResult(input);
    return [
      result === "win"
        ? "What made the mating pattern work? Trace the checks and the escape squares the opponent lost."
        : result === "loss"
          ? "Before the decisive threat, which defense would you investigate? Calculate the opponent’s reply."
          : result === "stalemate"
            ? "Which pieces and pawns remove the stalemated king’s last escape squares?"
            : "Which decision most changed the game, and what alternatives would you compare in the recap?",
      "Replay one turning point. Name a candidate and the opponent’s strongest reply before moving.",
    ];
  }
  const out: string[] = [];
  // Safety comes before a strategic reflection; otherwise three lesson
  // questions can crowd the immediate threat out of the card entirely.
  if (readingEnd.inCheck) {
    out.push(
      "Your king is in check. Compare capturing the checker, blocking the line, and moving the king. Which legal reply is safest?"
    );
  } else if (readingEnd.looseWhite.length) {
    out.push(
      `The opponent attacks your ${readingEnd.looseWhite[0]}. Can it be captured safely, or is there a tactical reason it survives? Calculate one reply.`
    );
  }
  const repair =
    input.moves.find((move) => move.qualityBand === "tactical-error") ||
    input.moves.find((move) => move.qualityBand === "questionable");
  if (repair) {
    out.push(
      `Revisit ${moveRef(repair)}. Which opposing check, capture or threat needed an answer before your plan could work?`
    );
  }
  const deviation = firstDeparture(input);
  const alternative = latestAlternative(input);
  if (!repair && deviation?.courseSan) {
    out.push(
      `Compare ${deviation.san} with the course's ${deviation.courseSan} on move ${deviation.moveNumber}. Which square or defender does each move change?`
    );
  } else if (!repair && alternative) {
    const name = variationName(alternative);
    const course =
      alternative.courseSan && alternative.courseSan !== alternative.san
        ? ` How does its plan differ from the course's ${alternative.courseSan}?`
        : " Which piece or pawn break gives this plan its direction?";
    out.push(`${moveRef(alternative)} followed ${name || "a prepared alternative"}.${course}`);
  } else if (!repair) {
    const lesson = [...input.moves]
      .reverse()
      .find((move) => onPreparedRoad(move) && move.lessonWhy);
    if (lesson)
      out.push(
        `After ${moveRef(lesson)}, which of your pieces benefited most, and what would the opponent like to change?`
      );
  }
  if (readingEnd.character === "closed") {
    const breaks = readingEnd.pawnBreaks;
    out.push(
      breaks.length
        ? `The center is closed. Pawn breaks you can prepare: ${breaks.join(", ")}. Which one belongs in the plan, and what must be ready first?`
        : "The center is closed. Which pawn break belongs to you, and which to the opponent? Name both before choosing a plan."
    );
  } else if (readingEnd.character === "open") {
    const file = readingEnd.files.open[0];
    out.push(
      file
        ? `The center is open. The ${file}-file is open: which of your rooks can reach it, and what is the entry square?`
        : "The center is open. Which file do your rooks want, and what is the entry square at the end of it?"
    );
  } else if (readingEnd.pawnTension) {
    out.push(
      "Tension remains in the center. Who benefits if it resolves now, and who benefits from waiting one more move?"
    );
  } else if (readingEnd.pawnsTraded) {
    const lines = [...readingEnd.files.open, ...readingEnd.files.halfOpenWhite];
    out.push(
      lines.length
        ? `Pawn exchanges have opened lines: ${joinWords(lines)}. Which offers a useful target or entry square, and which pawn break could change that?`
        : "Pawn exchanges have changed the structure. Which open line or outpost can your pieces use, and which pawn break could change it again?"
    );
  } else {
    const breaks = readingEnd.pawnBreaks;
    out.push(
      breaks.length
        ? `No pawns have been traded yet. Breaks you can prepare: ${breaks.join(", ")}. What must be in place before you play one?`
        : "No pawns have been traded yet. Which pawn break do you want, and what must be in place before you play it?"
    );
  }
  if (out.length < 3) {
    out.push(
      "Which of your pieces is doing the least, and what is its best square? Check that improving it leaves no threat unanswered."
    );
  }
  if (weaknesses.some((w) => w.title.includes("hint"))) {
    out.splice(
      readingEnd.inCheck || readingEnd.looseWhite.length || repair ? 1 : 0,
      0,
      "When you reached for a hint, which step unlocked the move: the goal, the piece, or the candidates? That is the step to practice."
    );
  }
  const seen = new Set<string>();
  return out.filter((line) => (seen.has(line) ? false : (seen.add(line), true))).slice(0, 3);
}

function buildNextFive(
  input: ReportCardInput,
  readingEnd: PositionReading,
  weaknesses: ReportCardItem[]
): ReportCard["nextFive"] {
  const steps: string[] = [];
  const pendingThemes = input.themesAfter.filter((t) => t.state === "pending");
  const upcomingThemes =
    input.onMainline === false
      ? []
      : pendingThemes
          .filter((t) => t.mainlineMove > input.toMove && t.mainlineMove <= input.toMove + 5)
          .slice(0, 3);
  let headline: string;
  const departed = firstDeparture(input);
  const alternative = latestAlternative(input);

  if (input.gameOver) {
    headline = "The game is over";
    const result = gameResult(input);
    steps.push(
      result === "win"
        ? "You delivered checkmate. In the recap, find the earlier move that made the mating pattern possible."
        : result === "loss"
          ? "Your king was checkmated. In the recap, compare a defensive move before the mating threat with the move played."
          : result === "stalemate"
            ? "Stalemate is a draw: the side to move has no legal move and is not in check. Rewind and look for a way to keep play alive."
            : result === "draw"
              ? "The game ended in a draw. Find a position where a different plan could have kept more possibilities alive."
              : "Use the recap to compare the game's turning point with a different candidate move."
    );
    return {
      headline,
      steps,
      habit:
        "Write down one pattern from this game. Replay it from both sides, explaining each reply.",
    };
  }

  if (input.freePlay || input.toMove >= 15) {
    headline = "Into the middlegame";
    if (input.middlegamePlan) steps.push(input.middlegamePlan);
    if (readingEnd.pawnBreaks.length) {
      steps.push(
        `Candidate pawn breaks: ${readingEnd.pawnBreaks.join(", ")}. Compare the lines each would open before preparing one.`
      );
    }
    if (readingEnd.files.open.length || readingEnd.files.halfOpenWhite.length) {
      const file = readingEnd.files.open[0] || readingEnd.files.halfOpenWhite[0];
      steps.push(
        `Investigate the ${file}-file for a rook. Is there a target or safe entry square that makes it useful?`
      );
    }
    if (readingEnd.development.whiteUndeveloped.length) {
      steps.push(
        `Still on starting squares: ${joinWords(readingEnd.development.whiteUndeveloped)}. Can one join the plan without leaving a tactical weakness?`
      );
    }
    if (input.guide) steps.push(input.guide.next);
    steps.push("Check the opponent’s checks, captures and threats before committing to a plan.");
  } else if (input.mode === "recall") {
    headline = "Find the next plan from memory";
    steps.push(
      "Find the next decision from memory: name the opponent's threat, your least active piece, and the goal of your next move. The course move stays hidden."
    );
  } else if (input.upcoming.length && input.nextDecision) {
    headline = "The course's next ideas";
    steps.push(`First: ${input.nextDecision}`);
    for (const theme of upcomingThemes) steps.push(`${theme.title}: ${theme.idea}`);
    // Later watches assume positions not yet reached.
    if (input.upcoming[0]?.watch) steps.push(`Watch for: ${input.upcoming[0].watch}`);
  } else {
    headline =
      !departed && alternative ? "The alternative you chose" : "Rebuilding from this position";
    if (input.guide) {
      steps.push(input.guide.goal, input.guide.next, `Watch for: ${input.guide.watch}`);
    } else if (alternative) {
      steps.push(
        `Continue the plan of ${variationName(alternative) || "your chosen branch"}, checking the opponent’s latest threat first.`
      );
    }
    if (!departed && alternative && readingEnd.pawnBreaks.length) {
      steps.push(
        `Candidate pawn breaks: ${readingEnd.pawnBreaks.join(", ")}. Check which lines each would open.`
      );
    }
    // The course's main-line ideas only apply while the game is on that line.
    for (const theme of input.onMainline === false ? [] : pendingThemes.slice(0, 2)) {
      steps.push(`Still available from the course: ${theme.title}. ${theme.idea}`);
    }
  }

  let habit: string;
  const titles = weaknesses.map((w) => w.title).join(" ");
  if (/tactic/.test(titles)) {
    habit = "Before every move, name the opponent’s best capture and best check. Only then choose.";
  } else if (/hint/.test(titles)) {
    habit = "Say the goal and the piece out loud before touching the hint button.";
  } else if (/left the course/.test(titles)) {
    habit = "Compare the purposes of the course move and your candidate before choosing.";
  } else if (!departed && alternative) {
    habit = "Name your plan — the course line or this alternative — and its next goal.";
  } else if (/piece moved twice|queen came out|flank pawn/.test(titles)) {
    habit =
      "Count the pieces still on their starting squares before any second move of a developed piece.";
  } else if (/taken back/.test(titles)) {
    habit = "Calculate the opponent’s reply before committing to the move.";
  } else {
    habit = "After the opponent moves, name their next threat before choosing your plan.";
  }

  return { headline, steps: steps.filter(Boolean).slice(0, 6), habit };
}

function headlineFor(word: GradeWord, input: ReportCardInput): string {
  const count = input.moves.length;
  if (input.gameOver) {
    return count === 5
      ? "The game ended during this stretch. Review the last five moves."
      : `The game ended during this stretch. Review ${count === 1 ? "the last move" : `these ${count} moves`}.`;
  }
  switch (word) {
    case "Composed":
      return "Purposeful play. Connect these moves to a plan you can explain.";
    case "Steady":
      return "A steady stretch. Keep the useful ideas and tighten the decisions below.";
    case "Searching":
      return "Some ideas worked; others need another look. Start with the clearest turning point.";
    default:
      return "A difficult stretch. Revisit one decision at a time and calculate the reply.";
  }
}

function closingFor(input: ReportCardInput, word: GradeWord): string {
  if (input.gameOver) return "Read the final position once more before the recap.";
  if (input.finalWindow && !input.freePlay) {
    return "Fifteen moves done. Carry the opening’s ideas into the position you have, rather than following a sequence by habit.";
  }
  if (input.windowIndex === 1) {
    return input.moves.length === 5
      ? "Your first five exchanges are done. Choose one lesson to take back to the board."
      : "Your first exchanges are done. Choose one lesson to take back to the board.";
  }
  if (word === "Unsettled" || word === "Searching") {
    return "Pause before committing: calculate the reply to your candidate.";
  }
  return "Take a breath. The board will wait.";
}

export function buildReportCard(input: ReportCardInput): ReportCard {
  const actualReadingStart = readPosition(input.fenStart, input.playerSide);
  const actualReadingEnd = readPosition(input.fenEnd, input.playerSide);
  const own = (reading: PositionReading): PositionReading =>
    input.playerSide !== "b"
      ? reading
      : {
          ...reading,
          development: {
            ...reading.development,
            whiteOut: reading.development.blackOut,
            whiteTotal: reading.development.blackTotal,
            whiteUndeveloped: reading.development.blackUndeveloped || [],
            whiteRooksConnected: Boolean(reading.development.blackRooksConnected),
          },
          files: { ...reading.files, halfOpenWhite: reading.files.halfOpenBlack },
          looseWhite: reading.looseBlack || [],
        };
  const readingStart = own(actualReadingStart);
  const readingEnd = {
    ...own(actualReadingEnd),
    inCheck:
      !input.gameOver &&
      actualReadingEnd.sideToMove === (input.playerSide ?? "w") &&
      actualReadingEnd.inCheck,
  };
  const scores = input.moves.map(moveScore);
  const score = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  // A tactical error is never "composed": it moves the word one notch down.
  const tacticalErrors = input.moves.filter((m) => m.qualityBand === "tactical-error").length;
  const ladder: GradeWord[] = ["Composed", "Steady", "Searching", "Unsettled"];
  const word = tacticalErrors
    ? ladder[Math.min(ladder.length - 1, ladder.indexOf(gradeWord(score)) + 1)]
    : gradeWord(score);
  const inBook = input.moves.filter((m) => m.isMainline).length;
  const alternatives = input.moves.filter((m) => !m.isMainline && onPreparedRoad(m)).length;
  const medals = input.moves.filter((m) => m.medal).length;
  const unaided = input.moves.filter((m) => !m.assisted).length;
  const percentiles = input.moves
    .map((m) => m.percentile)
    .filter((value): value is number => value != null);
  const averagePercentile = percentiles.length
    ? Math.round(percentiles.reduce((a, b) => a + b, 0) / percentiles.length)
    : null;
  const summaryParts = [
    alternatives
      ? `${inBook} of ${input.moves.length} in the course line, plus ${plural(alternatives, "recognized alternative")}`
      : `${inBook} of ${input.moves.length} in the course line`,
    input.engineChecked ? plural(medals, "medal") : "engine not checked",
    `${unaided} found unaided`,
    ...(averagePercentile != null ? [`average rank: top ${averagePercentile}%`] : []),
  ];

  const points = input.moves
    .filter((m) => m.evalAfterCp != null)
    .map((m) => ({
      moveNumber: m.moveNumber,
      cp: (m.evalAfterCp as number) * (input.playerSide === "b" ? -1 : 1),
    }));
  let trendSummary =
    "The engine was not available for this stretch, so there is no evaluation trend to show.";
  if (points.length >= 2) {
    const first = points[0].cp;
    const last = points[points.length - 1].cp;
    const delta = last - first;
    const qualifier =
      delta >= 40 ? "evaluation rose" : delta <= -40 ? "evaluation fell" : "roughly unchanged";
    const low = points.reduce((min, p) => (p.cp < min.cp ? p : min), points[0]);
    const dip =
      low.cp < Math.min(first, last) - 100
        ? ` It dipped to ${formatCp(low.cp)} after move ${low.moveNumber} along the way.`
        : "";
    trendSummary = `From the engine's view, ${input.playerSide === "b" ? "Black" : "White"} went from ${formatCp(first)} after move ${points[0].moveNumber} to ${formatCp(last)} after move ${points[points.length - 1].moveNumber}: ${qualifier}.${dip}`;
  } else if (points.length === 1) {
    trendSummary = `The engine's view after move ${points[0].moveNumber}: ${formatCp(points[0].cp)} for ${input.playerSide === "b" ? "Black" : "White"}.`;
  }

  const strengths = buildStrengths(input, readingStart, readingEnd);
  const weaknesses = buildWeaknesses(input, readingEnd);
  const reflections = buildReflections(input, readingEnd, weaknesses);
  const nextFive = buildNextFive(input, readingEnd, weaknesses);
  const summary = themeSummary(input.themesAfter);

  return {
    id: `${input.openingId}:w${input.windowIndex}`,
    playerSide: input.playerSide ?? "w",
    windowIndex: input.windowIndex,
    fromMove: input.fromMove,
    toMove: input.moves.at(-1)?.moveNumber ?? input.toMove,
    title: `Moves ${input.fromMove}–${input.moves.at(-1)?.moveNumber ?? input.toMove}`,
    openingTitle: input.openingTitle,
    grade: { word, score, summary: summaryParts.join(" · ") },
    headline: headlineFor(word, input),
    moves: input.moves.map((m, index) => ({
      moveNumber: m.moveNumber,
      san: m.san,
      band: m.qualityBand,
      bandLabel: QUALITY_BANDS[m.qualityBand].label,
      medal: m.medal,
      rank: m.rank,
      rankTotal: m.rankTotal ?? null,
      inBook: onPreparedRoad(m),
      bookLabel: onPreparedRoad(m)
        ? m.bookKind === "transposition" || m.bookStatus === "transposition"
          ? "Transposed"
          : m.isMainline
            ? "Course"
            : "Variation"
        : null,
      assisted: m.assisted,
      reply: input.blackReplies[index] || null,
    })),
    trend: { available: points.length >= 2, points, summary: trendSummary },
    strengths,
    weaknesses,
    reflections,
    reading: actualReadingEnd,
    themes: {
      done: summary.done,
      total: summary.total,
      newlyAchieved: newlyAchievedThemes(input.themesBefore, input.themesAfter),
      achieved: summary.achieved,
      // Ideas still ahead name the coming course moves, so From memory keeps
      // them back; away from the main line they are not this game's ideas.
      pending: input.mode === "recall" || input.onMainline === false ? [] : summary.pending,
      missed: summary.missed,
    },
    nextFive,
    closing: closingFor(input, word),
    finalWindow: input.finalWindow,
    freePlay: input.freePlay,
    gameOver: input.gameOver,
  };
}

/** A compact record for the progress page and the session recap. */
export type ReportCardSummary = {
  /** Session and window; a replayed window replaces its earlier entry. */
  key?: string;
  at: number;
  openingId: string;
  openingTitle: string;
  title: string;
  word: GradeWord;
  score: number;
  headline: string;
};

export function summarizeReportCard(
  card: ReportCard,
  openingId: string,
  at = Date.now()
): ReportCardSummary {
  return {
    at,
    openingId,
    openingTitle: card.openingTitle,
    title: card.title,
    word: card.grade.word,
    score: card.grade.score,
    headline: card.headline,
  };
}
